import i18n from '@/utils/i18n';
import moment, { Moment } from 'moment-timezone';
import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from '@/experiences/sales-portal/components/button';
import { Calendar } from '@usepepper/ui/drafts/calendar';
import { createStyles } from '@/contexts/theme-context';
import {
  DateValidationResult,
  TimeslotCategories,
} from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-fulfillment-selection';
import { Dropdown } from '@usepepper/ui/atoms/dropdown';
import { DropdownTrigger } from '@usepepper/ui/drafts/dropdown-trigger';
import { formatTimeslot } from '@/utils/date';
import { FulfillmentName, GetAvailableOrderFulfillmentResponse } from '@/types';
import { ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP } from '@domain/fulfillment-settings/blocs/order-availability';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import { ScrollView, View } from 'react-native';
import { SegmentedControl } from '@usepepper/ui/molecules/segmented-control';
import {
  Timeslot,
  TimeZoneMap,
} from '@domain/fulfillment-settings/types/types';
import { TimeslotOptionItem } from '@/experiences/sales-portal/domain/ordering/components/timeslot-option-item';
import { Typography } from '@usepepper/ui/atoms/typography';

export interface FulfillmentDropdownTriggerProps {
  availableFulfillments: GetAvailableOrderFulfillmentResponse[];
  disabled?: boolean;
  isOpen: boolean;
  onPress: () => void;
  onRefetch?: () => void;
  restaurantUUID: string;
  savedSelectedFulfillment: SavedSelectedFulfillment | null;
  selectableFulfillmentOptions: GetAvailableOrderFulfillmentResponse[];
  timezones: TimeZoneMap;
}

interface FulfillmentDropdownProps {
  availableFulfillments: GetAvailableOrderFulfillmentResponse[];
  createIsDateDisabled: (
    type: FulfillmentName | null,
  ) => (date: Moment) => boolean;
  disabled?: boolean;
  getTimeslotCategories: (
    date: string | null,
    type: FulfillmentName | null,
  ) => TimeslotCategories;
  onClose?: () => void;
  onOpen?: () => void;
  onRefetch?: () => void;
  onSave: (fulfillment: SavedSelectedFulfillment) => void;
  restaurantUUID: string;
  savedSelectedFulfillment: SavedSelectedFulfillment | null;
  selectableFulfillmentOptions: GetAvailableOrderFulfillmentResponse[];
  timezones: TimeZoneMap;
  trigger: React.ComponentType<FulfillmentDropdownTriggerProps>;
  validateFulfillmentDate: (
    date: string,
    type: FulfillmentName,
  ) => DateValidationResult;
  validateTimeslot: (
    timeslot: Timeslot | null,
    date: Moment,
    type: FulfillmentName,
  ) => Timeslot | null;
}

/**
 * Calendar-based fulfillment selector for Sales Portal order entry.
 *
 * This component owns its pending state (uncommitted changes while dropdown is open).
 * Changes are only persisted when the user clicks Save.
 *
 * Features:
 * - Displays available fulfillment types (Delivery/Pickup) via segmented control
 * - Shows calendar with disabled dates based on cutoff times, lead times, and timeslots
 * - Timeslot selection when available for selected date
 *
 * State Management:
 * - pendingFulfillment: Tracks uncommitted changes (type, date, timeslot)
 * - showDropdown/showTimeslotDropdown: UI visibility state
 *
 * On Save: Calls onSave with pendingFulfillment
 * On Close (without Save): Discards pendingFulfillment
 */
export const FulfillmentDropdown = memo<FulfillmentDropdownProps>(
  ({
    availableFulfillments,
    selectableFulfillmentOptions,
    createIsDateDisabled: getIsDateDisabled,
    disabled,
    getTimeslotCategories,
    onClose,
    onOpen,
    onRefetch,
    onSave,
    restaurantUUID,
    savedSelectedFulfillment,
    timezones,
    trigger: TriggerComponent,
    validateFulfillmentDate,
    validateTimeslot,
  }) => {
    const [styles, theme] = useStyles();

    // UI visibility state
    const [showDropdown, setShowDropdown] = useState(false);
    const [showTimeslotDropdown, setShowTimeslotDropdown] = useState(false);

    // Pending state - only exists while dropdown is open
    const [pendingFulfillment, setPendingFulfillment] =
      useState<SavedSelectedFulfillment | null>(null);

    // Open dropdown and initialize pending state from saved state.
    // We check `disabled` inside the handler as a fallback guard. The trigger
    // component handles the primary disabled state, but this ensures the dropdown
    // cannot be opened programmatically if disabled.
    const handleOpenDropdown = useCallback(() => {
      if (disabled) {
        return;
      }
      setPendingFulfillment(savedSelectedFulfillment);
      setShowDropdown(true);
      onOpen?.();
      onRefetch?.();
    }, [disabled, onRefetch, savedSelectedFulfillment, onOpen]);

    // Close dropdown and discard pending changes
    const handleCloseDropdown = useCallback(() => {
      setPendingFulfillment(null);
      setShowDropdown(false);
      setShowTimeslotDropdown(false);
      onClose?.();
    }, [onClose]);

    // Save pending changes and close dropdown
    const handleSaveAndClose = useCallback(() => {
      if (pendingFulfillment) {
        onSave(pendingFulfillment);
      }
      setPendingFulfillment(null);
      setShowDropdown(false);
      setShowTimeslotDropdown(false);
      onClose?.();
    }, [onSave, pendingFulfillment, onClose]);

    /**
     * Handle fulfillment type change.
     * Validates current date against the new type - if invalid, finds next available date.
     * Validates timeslot against the new date's available timeslots.
     *
     * TODO (DSR-3504): move validation logic to fulfillments domain/bloc to reduce coupling
     */
    const handleTypeChange = useCallback(
      (type: FulfillmentName) => {
        const currentDate = pendingFulfillment?.date;
        const currentTimeslot = pendingFulfillment?.timeslot;
        const selectedFulfillment = availableFulfillments.find(
          (f) => f.fulfillment_name === type,
        );
        const fulfillmentSettingsUUID =
          selectedFulfillment?.fulfillment_settings_id || null;

        if (!currentDate) {
          setPendingFulfillment({
            date: '',
            fulfillmentSettingsUUID,
            timeslot: null,
            type,
          });
          return;
        }

        // Validate current date for the new type
        const { isValid, nextValidDate } = validateFulfillmentDate(
          currentDate,
          type,
        );
        const dateMoment = moment(currentDate).tz(timezones.restaurant);

        if (isValid) {
          // Date is valid - validate timeslot against new type's options
          const validatedTimeslot = validateTimeslot(
            currentTimeslot ?? null,
            dateMoment,
            type,
          );
          setPendingFulfillment({
            date: currentDate,
            fulfillmentSettingsUUID,
            timeslot: validatedTimeslot,
            type,
          });
        } else {
          // Date is invalid for new type - find next available, clear timeslot
          setPendingFulfillment({
            date: nextValidDate.toISOString(),
            fulfillmentSettingsUUID,
            timeslot: null,
            type,
          });
        }
        setShowTimeslotDropdown(false);
      },
      [
        availableFulfillments,
        pendingFulfillment?.date,
        pendingFulfillment?.timeslot,
        timezones.restaurant,
        validateFulfillmentDate,
        validateTimeslot,
      ],
    );

    /**
     * Handle date selection from calendar.
     * Validates timeslot against the new date's available timeslots.
     */
    const handleDateChange = useCallback(
      (date: Moment) => {
        const restaurantDate = moment.tz(
          date.format('YYYY-MM-DD'),
          timezones.restaurant,
        );

        const pendingType =
          pendingFulfillment?.type ||
          selectableFulfillmentOptions[0]?.fulfillment_name;
        const currentTimeslot = pendingFulfillment?.timeslot;
        const selectedFulfillment = availableFulfillments.find(
          (f) => f.fulfillment_name === pendingType,
        );

        // Validate timeslot against new date's available options for pending type
        const validatedTimeslot = pendingType
          ? validateTimeslot(
              currentTimeslot ?? null,
              restaurantDate,
              pendingType,
            )
          : null;

        setPendingFulfillment({
          date: restaurantDate.toISOString(),
          fulfillmentSettingsUUID:
            selectedFulfillment?.fulfillment_settings_id || null,
          timeslot: validatedTimeslot,
          type: pendingType,
        });
      },
      [
        availableFulfillments,
        pendingFulfillment?.timeslot,
        pendingFulfillment?.type,
        selectableFulfillmentOptions,
        timezones.restaurant,
        validateTimeslot,
      ],
    );

    // Handle timeslot selection
    const handleTimeslotSelect = (timeslot: Timeslot) => {
      setPendingFulfillment((prev) => (prev ? { ...prev, timeslot } : null));
      setShowTimeslotDropdown(false);
    };

    // Get categorized timeslots for the pending type and date
    const { availableTimeslots, disabledTimeslots, hasTimeslots } =
      getTimeslotCategories(
        pendingFulfillment?.date ?? null,
        pendingFulfillment?.type ?? null,
      );

    // Compute isSaveDisabled - only require type and date (matches Operator Portal)
    const isSaveDisabled =
      !pendingFulfillment?.type || !pendingFulfillment?.date;

    // Get isDateDisabled for the pending type
    const isDateDisabled = useMemo(() => {
      return getIsDateDisabled(pendingFulfillment?.type ?? null);
    }, [getIsDateDisabled, pendingFulfillment?.type]);

    // Active date for calendar display
    const activeDateIso =
      pendingFulfillment?.date || savedSelectedFulfillment?.date;
    const activeDate = activeDateIso
      ? moment(
          moment(activeDateIso).tz(timezones.restaurant).format('YYYY-MM-DD'),
        )
      : null;

    // Selected type for segmented control
    const selectedType =
      pendingFulfillment?.type ||
      savedSelectedFulfillment?.type ||
      selectableFulfillmentOptions[0]?.fulfillment_name;

    // Render the time dropdown below calendar when timeslots are available
    const renderTimeslotDropdown = () => {
      if (!hasTimeslots) {
        return null;
      }

      const displayTimeslot = pendingFulfillment?.timeslot;
      const selectedTimeslotDisplay = displayTimeslot
        ? formatTimeslot(displayTimeslot, timezones, true)
        : i18n.t('sales_portal.fulfillment_dropdown.time_placeholder');

      const timeslotTriggerElement = (
        <DropdownTrigger
          isOpen={showTimeslotDropdown}
          onPress={() => setShowTimeslotDropdown(!showTimeslotDropdown)}
          style={styles.timeslotTrigger}
        >
          <Typography
            color={
              displayTimeslot
                ? theme.sk.color.text.primary
                : theme.sk.color.text.tertiary
            }
            variant="paragraph-md"
          >
            {selectedTimeslotDisplay}
          </Typography>
        </DropdownTrigger>
      );

      return (
        <View style={styles.timeSelectorContainer}>
          <Typography color={theme.sk.color.text.secondary} variant="label-md">
            {i18n.t('sales_portal.fulfillment_dropdown.time_label')}
          </Typography>
          <Dropdown
            containerStyle={styles.timeslotDropdownContent}
            isVisible={showTimeslotDropdown}
            onClose={() => setShowTimeslotDropdown(false)}
            position="bottom-start"
            trigger={timeslotTriggerElement}
          >
            <ScrollView style={styles.timeslotList}>
              {availableTimeslots.map((option) => (
                <TimeslotOptionItem
                  displayText={
                    option.metadata?.timeslot
                      ? formatTimeslot(
                          option.metadata.timeslot,
                          timezones,
                          true,
                        )
                      : option.title
                  }
                  key={`${option.metadata?.timeslot.startTime}-${option.metadata?.timeslot.endTime}`}
                  onPress={() => {
                    if (option.metadata?.timeslot) {
                      handleTimeslotSelect(option.metadata.timeslot);
                    }
                  }}
                />
              ))}
              {disabledTimeslots.length > 0 ? (
                <>
                  <View style={styles.timeslotSectionHeader}>
                    <Typography
                      color={theme.sk.color.text.secondary}
                      variant="paragraph-sm-600"
                    >
                      {i18n.t('past')}
                    </Typography>
                  </View>
                  {disabledTimeslots.map((option) => (
                    <TimeslotOptionItem
                      displayText={
                        option.metadata?.timeslot
                          ? formatTimeslot(
                              option.metadata.timeslot,
                              timezones,
                              true,
                            )
                          : option.title
                      }
                      isDisabled
                      key={`disabled-${option.metadata?.timeslot.startTime}-${option.metadata?.timeslot.endTime}`}
                    />
                  ))}
                </>
              ) : null}
            </ScrollView>
          </Dropdown>
        </View>
      );
    };

    const renderSegmentedControl = () => {
      if (selectableFulfillmentOptions.length < 2) {
        return null;
      }

      const leftOption = (
        <Typography
          color={theme.sk.color.text.primary}
          variant="paragraph-md-600"
        >
          {selectableFulfillmentOptions[0].fulfillment_display_name ||
            ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP[
              selectableFulfillmentOptions[0].fulfillment_name
            ]}
        </Typography>
      );

      const rightOption = (
        <Typography
          color={theme.sk.color.text.primary}
          variant="paragraph-md-600"
        >
          {selectableFulfillmentOptions[1].fulfillment_display_name ||
            ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP[
              selectableFulfillmentOptions[1].fulfillment_name
            ]}
        </Typography>
      );

      return (
        <View style={styles.segmentedControlBackground}>
          <SegmentedControl
            options={[
              {
                accessibilityLabel:
                  selectableFulfillmentOptions[0].fulfillment_display_name ||
                  ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP[
                    selectableFulfillmentOptions[0].fulfillment_name
                  ],
                children: leftOption,
                key: selectableFulfillmentOptions[0].fulfillment_name,
                onPress: () =>
                  handleTypeChange(
                    selectableFulfillmentOptions[0].fulfillment_name,
                  ),
              },
              {
                accessibilityLabel:
                  selectableFulfillmentOptions[1].fulfillment_display_name ||
                  ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP[
                    selectableFulfillmentOptions[1].fulfillment_name
                  ],
                children: rightOption,
                key: selectableFulfillmentOptions[1].fulfillment_name,
                onPress: () =>
                  handleTypeChange(
                    selectableFulfillmentOptions[1].fulfillment_name,
                  ),
              },
            ]}
            selectedOption={selectedType}
          />
        </View>
      );
    };

    return (
      <Dropdown
        containerStyle={styles.dropdownContent}
        isVisible={showDropdown}
        onClose={handleCloseDropdown}
        position="bottom-start"
        trigger={
          <TriggerComponent
            availableFulfillments={availableFulfillments}
            disabled={disabled}
            isOpen={showDropdown}
            onPress={handleOpenDropdown}
            onRefetch={onRefetch}
            restaurantUUID={restaurantUUID}
            savedSelectedFulfillment={savedSelectedFulfillment}
            selectableFulfillmentOptions={selectableFulfillmentOptions}
            timezones={timezones}
          />
        }
      >
        {renderSegmentedControl()}
        <Calendar
          activeDate={activeDate}
          isDateDisabled={isDateDisabled}
          // key ensures Calendar fully remounts when type changes,
          // resetting any internal state for disabled dates
          key={selectedType}
          onDateChange={handleDateChange}
        />
        {renderTimeslotDropdown()}
        <Button
          accessibilityLabel={i18n.t('save')}
          disabled={isSaveDisabled}
          onPress={handleSaveAndClose}
          size={ButtonSize.Small}
          style={styles.saveButton}
          variant={ButtonVariant.Primary}
        >
          {i18n.t('save')}
        </Button>
      </Dropdown>
    );
  },
);

const MIN_DROPDOWN_WIDTH = 345;

const useStyles = createStyles((theme) => ({
  dropdownContent: {
    gap: theme.sk.spacing.spaceSmall,
    minWidth: MIN_DROPDOWN_WIDTH,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  saveButton: {
    alignSelf: 'flex-end',
    minWidth: 104,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  segmentedControlBackground: {
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderRadius: 40,
    flexDirection: 'row',
    height: 40,
    padding: 4,
  },
  timeSelectorContainer: {
    gap: theme.sk.spacing.spaceXsmall,
  },
  timeslotDropdownContent: {
    padding: theme.sk.spacing.spaceSmall,
    width: MIN_DROPDOWN_WIDTH - 2 * theme.sk.spacing.spaceSMedium,
  },
  timeslotList: {
    gap: theme.sk.spacing.spaceXsmall,
    maxHeight: 200,
  },
  timeslotSectionHeader: {
    paddingTop: theme.sk.spacing.spaceSmall,
  },
  timeslotTrigger: {
    width: '100%',
  },
}));
